import { useEffect, useRef } from "react";
import type { CancelLine } from "../lib/session.ts";

/**
 * The last look before a session and its sets are deleted. Every line is
 * listed with what it holds, so a wrong tap in the gym reads as a wrong tap
 * before anything is lost.
 *
 * A native <dialog>: modal, focus-trapped and closed by the back gesture on
 * its own, with no portal to manage.
 */
export function CancelSessionDialog({
  open,
  title,
  lines,
  sets,
  pending,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  lines: CancelLine[];
  sets: number;
  pending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const logged = lines.filter((line) => line.logged !== null);
  const confirmLabel = sets > 0 ? `Supprimer ${sets} série${sets > 1 ? "s" : ""} et annuler` : "Annuler la séance";

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      // A tap on the backdrop lands on the dialog element itself.
      onClick={(event) => event.target === event.currentTarget && !pending && onClose()}
      className="m-auto w-[min(28rem,calc(100vw-2rem))] rounded-card border border-line bg-surface p-0 text-ink backdrop:bg-black/70"
    >
      <div className="px-5 pt-5 pb-4">
        <p className="label text-stop">Annuler la séance</p>
        <h2 className="wide mt-1 text-[1.25rem] font-bold">{title}</h2>
        <p className="mt-2 text-[0.9rem] text-ink-soft">
          {logged.length > 0
            ? "La séance disparaît de l'historique, avec tout ce qui y est saisi :"
            : "Rien n'y est saisi. La séance disparaît de l'historique."}
        </p>

        {logged.length > 0 && (
          <ul className="mt-3 max-h-[45vh] space-y-2 overflow-y-auto">
            {logged.map((line) => (
              <li key={line.id} className="rounded-card bg-surface-high px-3 py-2">
                <span className="block text-[0.95rem] font-semibold">{line.name}</span>
                <span className="num block text-[0.84rem] text-stop">{line.logged}</span>
              </li>
            ))}
          </ul>
        )}

        {logged.length > 0 && logged.length < lines.length && (
          <p className="mt-2 text-[0.82rem] text-ink-mute">
            Sans rien de saisi :{" "}
            {lines
              .filter((line) => line.logged === null)
              .map((line) => line.name)
              .join(", ")}
            .
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2 border-t border-line/60 px-5 py-4">
        <button
          type="button"
          disabled={pending}
          onClick={onConfirm}
          className="w-full rounded-card bg-stop px-4 py-3 text-[0.95rem] font-bold text-white active:brightness-90 disabled:opacity-60"
        >
          {pending ? "Annulation…" : confirmLabel}
        </button>
        <button
          type="button"
          disabled={pending}
          onClick={onClose}
          // Focused first: Enter or a stray tap keeps the session.
          autoFocus
          className="w-full rounded-card px-4 py-3 text-[0.95rem] text-ink-soft active:text-ink disabled:opacity-50"
        >
          Garder la séance
        </button>
      </div>
    </dialog>
  );
}
